import type { LocationMatch } from '@citibox-panel/api-client';
import type { AutocompleteOption } from '@citibox-panel/ui';
import { useDebouncedSearch } from '../../../lib/useDebouncedSearch';
import { locationSearchKey } from '../../../lib/locationSearchKey';

export interface UseAddressSuggestions {
  /**
   * Every row the read returned, shaped for the combobox and in the order it came. NOT capped and
   * NOT deduplicated by id: the cap is the field's rendering concern (the wrapper's `limit`), and a
   * building with two portals is two pickable rows that open the same location.
   */
  options: AutocompleteOption[];
  isTooShort: boolean;
  isSearching: boolean;
  isError: boolean;
}

// The ficha's address suggestions. It owns no read of its own: it leans on the shared debounce and
// the SHARED cache key, so the combobox reuses whatever the header's address search already
// fetched. Everything about failure here is «fail open» — see `isError`.
export function useAddressSuggestions(
  query: string,
  searchLocations: (q: string) => Promise<LocationMatch[]>,
): UseAddressSuggestions {
  const { data, isTooShort, isSearching, isError } = useDebouncedSearch({
    query,
    queryKey: locationSearchKey,
    search: searchLocations,
  });

  return {
    // Empty array and not `undefined`: the combobox always has an options list, and «nothing yet»
    // is told apart from «nothing matched» by `isSearching` / `isTooShort`, never by a missing prop.
    options: (data ?? []).map((match) => ({ id: match.id, label: match.address })),
    isTooShort,
    isSearching,
    // Surfaced, never thrown. A failed suggestions read disables ONE field; throwing would send
    // the whole ficha to its error boundary and take the half-filled form with it.
    isError,
  };
}
